import { useEffect, useState } from 'react';
import { message } from 'antd';
import api from '../../../system/client/src/services/api';

/* The org's saved traders (trader_id -> row) and a status changer, shared by
 * the results list and the results map so saving works the same from both.
 * Refetched when the result set changes. */
const useSavedTraders = (traders) => {
    const [savedTraders, setSavedTraders] = useState(new Map());

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!traders || traders.length === 0) {
                if (!cancelled) setSavedTraders(new Map());
                return;
            }
            try {
                // summary=true: DB rows only. Without it the server looks up
                // live trade details for EVERY saved trader on every new result
                // set, which emptied the DB pool for orgs with hundreds saved
                // (Oct 2026). Only trader_id and status are used here.
                const data = await api.get('saved-traders?summary=true');
                if (cancelled) return;
                // saved_traders.trader_id is a VARCHAR, so the summary sends
                // "223564" while result rows carry 223564: key by number, or
                // every saved trader shows as Not Saved and saving it again
                // fails with "already saved" (Oct 2026).
                const map = new Map();
                (data || []).forEach((s) => map.set(Number(s.trader_id), { ...s, trader_id: Number(s.trader_id) }));
                setSavedTraders(map);
            } catch (e) {
                console.error('Error fetching saved traders:', e);
                if (!cancelled) setSavedTraders(new Map());
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [traders]);

    const handleStatusChange = async (traderId, newStatus) => {
        try {
            if (newStatus === 'Not Saved') {
                await api.delete(`saved-traders/${traderId}`);
                setSavedTraders((prev) => {
                    const m = new Map(prev);
                    m.delete(traderId);
                    return m;
                });
                message.success('Removed from pipeline');
            } else {
                const existing = savedTraders.get(traderId);
                if (!existing) {
                    await api.post('saved-traders', {
                        traderId,
                        status: newStatus,
                        notes: JSON.stringify([]),
                    });
                    setSavedTraders((prev) => {
                        const m = new Map(prev);
                        m.set(traderId, { trader_id: traderId, status: newStatus });
                        return m;
                    });
                } else {
                    await api.put(`saved-traders/${traderId}/status`, {
                        status: newStatus,
                    });
                    setSavedTraders((prev) => {
                        const m = new Map(prev);
                        m.set(traderId, { ...existing, status: newStatus });
                        return m;
                    });
                }
                message.success('Status updated');
            }
        } catch (e) {
            console.error('Error updating status:', e);
            message.error('Failed to update status');
        }
    };

    return [savedTraders, handleStatusChange];
};

export default useSavedTraders;
